// src/components/PatentStrzelecki.jsx
// Strona filarowa pod frazę „patent strzelecki” - łączy ruch informacyjny
// z ofertą (testy) i linkuje do wszystkich zestawów pytań.
import React from 'react';
import { Link } from 'react-router-dom';
import styles from '../css/modules/Page.module.css';
import Faq from './Faq';
import JsonLd from '../seo/JsonLd';
import { faqJsonLd, breadcrumbJsonLd, EXAM_PAGES, getLowestPrice } from '../seo/siteMeta';
import { trackEvent } from '../lib/analytics';

const FAQ_ITEMS = [
  {
    question: 'Czym jest patent strzelecki?',
    answer:
      'Patent strzelecki to uprawnienie nadawane przez Polski Związek Strzelectwa Sportowego, potwierdzające znajomość przepisów i umiejętność bezpiecznego posługiwania się bronią sportową. Nadawany jest osobno dla dyscyplin: pistolet, karabin i strzelba.',
  },
  {
    question: 'Kto może przystąpić do egzaminu na patent strzelecki?',
    answer:
      'Do egzaminu przystępują osoby zrzeszone w klubie strzeleckim należącym do PZSS, po odbyciu przeszkolenia i treningów potwierdzonych przez klub. Szczegółowe warunki, w tym wymagany staż i liczba treningów, określa aktualny regulamin PZSS oraz zasady danego klubu.',
  },
  {
    question: 'Jak wygląda egzamin na patent strzelecki?',
    answer:
      'Egzamin składa się z części teoretycznej (test wiedzy z przepisów, bezpieczeństwa i regulaminów sportowych) oraz części praktycznej, w której kandydat wykonuje strzelania w wybranych dyscyplinach zgodnie z regulaminem PZSS.',
  },
  {
    question: 'Czego dotyczy część teoretyczna egzaminu?',
    answer:
      'Część teoretyczna obejmuje przepisy ustawy o broni i amunicji, wybrane przepisy karne dotyczące broni, zasady bezpieczeństwa i regulamin strzelnicy oraz podstawy przepisów sportowych ISSF i PZSS. Dokładnie ten zakres ćwiczysz w testach na Strzelasz.pl.',
  },
  {
    question: 'Czy patent strzelecki wystarczy do pozwolenia na broń?',
    answer:
      'Nie. Patent jest jednym z warunków. Do wniosku o pozwolenie na broń do celów sportowych potrzebna jest także aktualna licencja zawodnicza PZSS, orzeczenia lekarskie i psychologiczne oraz zaświadczenie o przynależności do klubu. Wniosek składa się do komendanta wojewódzkiego Policji.',
  },
  {
    question: 'Jak długo ważny jest patent strzelecki?',
    answer:
      'Patent strzelecki nadawany jest bezterminowo. Odnawiać trzeba natomiast licencję zawodniczą PZSS - co do zasady w cyklu rocznym, a jej przedłużenie wymaga udokumentowanego startu w zawodach.',
  },
  {
    question: 'Ile trzeba się uczyć do części teoretycznej?',
    answer:
      'Przy regularnej pracy - 15-30 pytań dziennie z powtórkami błędów - większość kandydatów opanowuje materiał w dwa-trzy tygodnie. Jeśli egzamin jest za kilka dni, sensowniejsza jest intensywna powtórka pełnych zestawów pytań w trybie egzaminacyjnym.',
  },
];

const PatentStrzelecki = () => {
  const lowestPrice = getLowestPrice();

  return (
    <>
      <JsonLd
        data={[
          faqJsonLd(FAQ_ITEMS),
          breadcrumbJsonLd([
            { name: 'Strona główna', path: '/' },
            { name: 'Patent strzelecki', path: '/patent-strzelecki' },
          ]),
        ]}
      />

      <main className={styles.page}>
        <article className={styles.wrap}>
          <nav className={styles.breadcrumbs} aria-label="Ścieżka nawigacji">
            <Link to="/">Strona główna</Link> › <span>Patent strzelecki</span>
          </nav>

          <h1 className={styles.h1}>
            Patent strzelecki 2026 - wymagania, egzamin i droga do pozwolenia na broń
          </h1>

          <p className={styles.lead}>
            Praktyczny przewodnik po <strong>patencie strzeleckim PZSS</strong>: kto może zdawać,
            jak wygląda egzamin teoretyczny i praktyczny, ile to trwa i co zrobić dalej, żeby
            uzyskać <strong>pozwolenie na broń do celów sportowych</strong>.
          </p>

          <p className={styles.updated}>
            Materiał edukacyjny serwisu Strzelasz.pl · aktualizacja: 2026
          </p>

          <nav className={styles.toc} aria-label="Spis treści">
            <h2>Spis treści</h2>
            <ol>
              <li><a href="#czym-jest">Czym jest patent strzelecki</a></li>
              <li><a href="#wymagania">Wymagania przed egzaminem</a></li>
              <li><a href="#egzamin">Jak wygląda egzamin</a></li>
              <li><a href="#teoria">Zakres części teoretycznej</a></li>
              <li><a href="#pozwolenie">Od patentu do pozwolenia na broń</a></li>
              <li><a href="#nauka">Jak się przygotować</a></li>
              <li><a href="#faq">Najczęstsze pytania</a></li>
            </ol>
          </nav>

          <section className={styles.section} id="czym-jest">
            <h2>Czym jest patent strzelecki</h2>
            <p>
              <strong>Patent strzelecki</strong> to uprawnienie nadawane przez Polski Związek
              Strzelectwa Sportowego (PZSS). Potwierdza, że kandydat zna przepisy dotyczące broni
              i zasady bezpieczeństwa oraz potrafi bezpiecznie posługiwać się bronią sportową.
              Patent nadawany jest w podziale na dyscypliny: <strong>pistolet</strong>,{' '}
              <strong>karabin</strong> i <strong>strzelba</strong> - można zdawać jedną, dwie
              lub wszystkie trzy.
            </p>
            <p>
              Dla większości osób patent to pierwszy formalny krok w stronę własnej broni
              sportowej. Sam w sobie nie uprawnia jeszcze do jej posiadania, ale bez niego
              ścieżka sportowa po pozwolenie jest zamknięta.
            </p>
          </section>

          <section className={styles.section} id="wymagania">
            <h2>Wymagania przed egzaminem na patent</h2>
            <p>
              Zanim złożysz wniosek o dopuszczenie do egzaminu, w praktyce musisz przejść przez
              następujące etapy:
            </p>
            <ul>
              <li>
                <strong>Zapisanie się do klubu strzeleckiego</strong> zrzeszonego w PZSS -
                to klub potwierdza Twoje przygotowanie i kieruje Cię na egzamin.
              </li>
              <li>
                <strong>Przeszkolenie i treningi</strong> pod okiem instruktora, udokumentowane
                w dzienniku treningowym klubu.
              </li>
              <li>
                <strong>Opanowanie części teoretycznej</strong> - przepisy o broni i amunicji,
                zasady bezpieczeństwa, regulamin strzelnicy, podstawy przepisów sportowych.
              </li>
              <li>
                <strong>Wniosek egzaminacyjny i opłata</strong> - składane zgodnie z aktualnymi
                zasadami PZSS, zwykle za pośrednictwem klubu.
              </li>
            </ul>
            <p className={styles.note}>
              Wymagany staż klubowy, minimalna liczba treningów i wysokość opłaty egzaminacyjnej
              bywają zmieniane. Przed zapisaniem się na egzamin potwierdź aktualne warunki
              w swoim klubie i w obowiązującym regulaminie PZSS.
            </p>
          </section>

          <section className={styles.section} id="egzamin">
            <h2>Jak wygląda egzamin na patent strzelecki</h2>
            <h3>Część teoretyczna</h3>
            <p>
              Test wiedzy sprawdzający znajomość przepisów: ustawy o broni i amunicji, przepisów
              karnych związanych z bronią, zasad bezpieczeństwa oraz regulaminów sportowych.
              To ta część najczęściej decyduje o niezdanym egzaminie - nie dlatego, że jest
              trudna, tylko dlatego, że kandydaci przychodzą na nią nieprzygotowani.
            </p>
            <h3>Część praktyczna</h3>
            <p>
              Strzelania wykonywane zgodnie z regulaminem, osobno dla każdej zgłoszonej
              dyscypliny. Oceniane jest zarówno bezpieczeństwo obsługi broni i wykonywanie komend
              prowadzącego strzelanie, jak i wynik na tarczy.
            </p>
          </section>

          <section className={styles.section} id="teoria">
            <h2>Zakres części teoretycznej - co dokładnie trzeba umieć</h2>
            <p>
              Materiał do części teoretycznej podzieliliśmy na dziewięć zestawów pytań
              z odpowiedziami i wyjaśnieniami. Możesz je przejrzeć bez zakładania konta:
            </p>
            <ul className={styles.linkList}>
              {EXAM_PAGES.map(page => (
                <li key={page.path}>
                  <Link to={page.path}>{page.label}</Link>
                </li>
              ))}
            </ul>
          </section>

          <div className={styles.ctaBox}>
            <h2>Sprawdź, ile już umiesz</h2>
            <p>
              Rozwiąż darmowy test na patent strzelecki i zobacz, jak wygląda nauka w formie
              pojedynku. Bez rejestracji i bez opłat.
            </p>
            <div className={styles.ctaRow}>
              <Link
                to="/intro"
                className={styles.primaryButton}
                onClick={() => trackEvent('cta_click', { location: 'pillar', label: 'demo' })}
              >
                Rozwiąż darmowy test
              </Link>
              <Link
                to="/cennik"
                className={styles.secondaryButton}
                onClick={() => trackEvent('cta_click', { location: 'pillar', label: 'cennik' })}
              >
                Cennik od {lowestPrice} zł
              </Link>
            </div>
          </div>

          <section className={styles.section} id="pozwolenie">
            <h2>Od patentu do pozwolenia na broń - kolejne kroki</h2>
            <ol>
              <li>
                <strong>Patent strzelecki PZSS</strong> - zdany egzamin teoretyczny i praktyczny.
              </li>
              <li>
                <strong>Licencja zawodnicza PZSS</strong> - nadawana na wniosek klubu, odnawiana
                w cyklu rocznym; jej utrzymanie wymaga startów w zawodach.
              </li>
              <li>
                <strong>Badania lekarskie i psychologiczne</strong> u uprawnionych lekarzy
                i psychologów, zakończone orzeczeniami o braku przeciwwskazań do dysponowania
                bronią.
              </li>
              <li>
                <strong>Wniosek do komendanta wojewódzkiego Policji</strong> o pozwolenie na broń
                do celów sportowych, wraz z zaświadczeniem o przynależności do klubu i licencją.
              </li>
              <li>
                <strong>Decyzja i zaświadczenie na zakup broni</strong>, a po zakupie -
                rejestracja broni w wyznaczonym terminie.
              </li>
            </ol>
            <p>
              Formalności po stronie Policji opisujemy szczegółowo w przewodniku{' '}
              <a href="/pozwolenie-na-bron/">pozwolenie na broń krok po kroku</a>, a bieżące
              pytania kandydatów omawiamy w <a href="/egzamin">bazie artykułów o egzaminie</a>.
            </p>
          </section>

          <section className={styles.section} id="nauka">
            <h2>Jak przygotować się do części teoretycznej</h2>
            <p>
              Teoria to jedyna część egzaminu, którą możesz w całości opanować w domu. Sprawdza
              się prosty schemat:
            </p>
            <ul>
              <li>
                <strong>Codziennie 15-30 pytań</strong> zamiast jednej długiej sesji raz
                w tygodniu - powtarzalność wygrywa z maratonem.
              </li>
              <li>
                <strong>Wracaj do błędów</strong>. Pytania, na które odpowiedziałeś źle, powinny
                wracać do Ciebie, aż zaczniesz odpowiadać automatycznie.
              </li>
              <li>
                <strong>Ćwicz w trybie egzaminu</strong> - pełny zestaw, na czas, bez podpowiedzi.
                To oswaja z presją na sali egzaminacyjnej.
              </li>
              <li>
                <strong>Czytaj wyjaśnienia</strong>, a nie tylko poprawne odpowiedzi. Zrozumiany
                przepis zostaje w głowie dłużej niż zapamiętana litera odpowiedzi.
              </li>
            </ul>
            <p>
              Dokładnie w takim rytmie działa <Link to="/">platforma Strzelasz.pl</Link>: zestawy
              pytań, wyjaśnienia, tryb egzaminu i powtórki błędów.
            </p>
          </section>

          <section className={styles.section} id="faq">
            <h2>Patent strzelecki - najczęstsze pytania</h2>
            <Faq items={FAQ_ITEMS} />
          </section>

          <div className={styles.ctaBox}>
            <h2>Zdaj teorię za pierwszym razem</h2>
            <p>
              Dostęp do pełnej bazy pytań egzaminacyjnych z wyjaśnieniami. Płatność jednorazowa,
              bez subskrypcji, dostęp od razu po opłaceniu.
            </p>
            <div className={styles.ctaRow}>
              <Link
                to="/cennik"
                className={styles.primaryButton}
                onClick={() => trackEvent('cta_click', { location: 'pillar_end', label: 'cennik' })}
              >
                Zobacz pakiety od {lowestPrice} zł
              </Link>
            </div>
          </div>
        </article>
      </main>
    </>
  );
};

export default PatentStrzelecki;
